import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Toaster } from 'sonner';
import { SessionProvider, setGameIdInURL, useSession } from './modules/session';
import { ThemeProvider, useTemplate } from './modules/theme';
import { WalletProvider, useWallet } from './modules/wallet';
import { ErrorBoundary, LoadingScreen, ExpiredScreen, useView } from './modules/shell';
import { emit } from './modules/postMessage';
import { createApiClient, getDesignSystem } from './modules/api';
import type { DesignSystemResult } from './modules/api/endpoints/designSystem';
import { config } from './config';
import { useChildListener, useClickToEdit } from '@game-platform/theme-constructor';
import { Lobby as ClassicLobby } from './templates/classic/Lobby';
import { PackPage as ClassicPackPage } from './templates/classic/Pack';
import { PackPage as ModernPackPage } from './templates/modern/Pack';

const DevConsole = lazy(() =>
  import('./modules/dev').then((m) => ({ default: m.DevConsole })),
);

const DesignSystemPage = lazy(() =>
  import('./pages/DesignSystem').then((m) => ({ default: m.DesignSystemPage })),
);

function Inner() {
  const session = useSession();
  const wallet = useWallet();
  const { view, goLobby, goGame, goExpired } = useView();
  const didInit = useRef(false);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(null);

  const template = useTemplate();

  const client = useMemo(
    () => createApiClient({ baseUrl: config.apiUrl, getToken: () => session.token }),
    [session.token],
  );

  useEffect(() => {
    if (didInit.current) return;

    if (!session.token) {
      didInit.current = true;
      emit('session_expired');
      goExpired();
      return;
    }

    // Wait for WalletProvider's first balance fetch to settle before routing.
    if (wallet.loading) return;

    didInit.current = true;

    if (wallet.error) {
      // Most likely a 401 / invalid token (backend converted auth failure to
      // a NetworkError-free string). Treat as expired session.
      emit('session_expired');
      goExpired();
      return;
    }

    emit('session_ready', {
      user_id: 0,
      balance_cents: wallet.balance_cents ?? 0,
    });
    if (session.gameId) goGame();
    else goLobby();
  }, [
    session.token,
    session.gameId,
    wallet.loading,
    wallet.error,
    wallet.balance_cents,
    goLobby,
    goGame,
    goExpired,
  ]);

  if (view === 'loading') return <LoadingScreen />;
  if (view === 'expired') return <ExpiredScreen />;

  if (template === 'modern') {
    // Modern collapses lobby+game into a single carousel page. The carousel
    // itself acts as the lobby — swiping between packs picks the active gameId.
    return (
      <ModernPackPage
        client={client}
        initialGameId={session.gameId ?? selectedOfferId ?? null}
      />
    );
  }

  if (view === 'lobby') {
    return (
      <ClassicLobby
        client={client}
        onSelect={(offerId) => {
          setSelectedOfferId(offerId);
          setGameIdInURL(offerId);
          goGame();
        }}
      />
    );
  }
  if (view === 'game') {
    const gameId = session.gameId ?? selectedOfferId;
    if (!gameId) return null;
    return (
      <ClassicPackPage
        client={client}
        gameId={gameId}
      />
    );
  }
  return null;
}

export function App() {
  useChildListener(); // no-op unless ?design_constructor=child
  useClickToEdit(); // ditto — wires click-to-edit selection in the Design Constructor preview

  // /design-system is a dev-only standalone route. We mount the full
  // provider stack here too — minus the Inner main flow which would
  // render the ExpiredScreen on missing token — so DevConsole can run
  // (some of its tabs need Session/Wallet contexts).
  if (config.devConsole && window.location.pathname === '/design-system') {
    return (
      <ErrorBoundary>
        <SessionProvider>
          <ThemeProvider>
            <WalletProvider>
              <Suspense fallback={<LoadingScreen />}>
                <DesignSystemPage />
              </Suspense>
              <Suspense fallback={null}>
                <DevConsole />
              </Suspense>
            </WalletProvider>
          </ThemeProvider>
        </SessionProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <SessionProvider>
        <ThemedShell>
          <WalletProvider>
            <Inner />
            <Toaster position="top-center" />
            {config.devConsole && (
              <Suspense fallback={null}>
                <DevConsole />
              </Suspense>
            )}
          </WalletProvider>
        </ThemedShell>
      </SessionProvider>
    </ErrorBoundary>
  );
}

/**
 * Bridges the backend per-operator design_system into ThemeProvider:
 * once the URL session token is parsed, fetch the operator's theme
 * once and feed it as the `designSystem` + `template` props. ThemeProvider
 * re-applies the v2 ConstructorValue + template whenever the props
 * transition from undefined to a value. Fetch is soft-fail
 * (`{ template: 'classic', designSystem: null }` on any error) so the
 * iframe still boots with Figma defaults if the call fails.
 */
function ThemedShell({ children }: { children: ReactNode }) {
  const session = useSession();
  const [resolved, setResolved] = useState<DesignSystemResult | undefined>(undefined);

  useEffect(() => {
    if (!session.token) return;
    const client = createApiClient({
      baseUrl: config.apiUrl,
      getToken: () => session.token,
    });
    let cancelled = false;
    void getDesignSystem(client).then((ds) => {
      if (!cancelled) setResolved(ds);
    });
    return () => {
      cancelled = true;
    };
  }, [session.token]);

  return (
    <ThemeProvider
      designSystem={resolved?.designSystem ?? undefined}
      template={resolved?.template}
    >
      {children}
    </ThemeProvider>
  );
}
